import { SnowflakeCompilerSqlTokenWords } from "@redotech/redo-analytics-custom-analytics/compilers/snowflake/v1/snowflake-query-compiler-sql-tokens";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";

export const TAB_QUERY_PARAM = "tab";
export const OVERVIEW_LOOKBACK_PARAM = "overviewLookbackDays";
export const OVERVIEW_TEAM_INPUT_PARAM = "overviewTeamInput";
export const OVERVIEW_TEAM_FILTER_PARAM = "overviewTeamId";
export const DEBUG_REPORT_ID_PARAM = "debugReportId";
export const DEBUG_TEAM_ID_PARAM = "debugTeamId";
export const CATALOG_SEARCH_PARAM = "catalogSearch";
export const RELATIONSHIPS_BASE_GRAIN_PARAM = "relationshipsBase";
export const RELATIONSHIPS_FIELD_PARAM = "relationshipsField";
export const RELATIONSHIPS_SEARCH_PARAM = "relationshipsSearch";

export const RELATIONSHIP_GRAPH_HORIZONTAL_GAP = 560;
export const RELATIONSHIP_GRAPH_VERTICAL_GAP = 210;
export const RELATIONSHIP_GRAPH_ACTIVE_COLOR =
  "var(--redo-colors-orange-dark-500)";
export const RELATIONSHIP_GRAPH_SUPPORT_COLOR =
  "var(--redo-colors-success-500)";

export interface RelationshipGraphGridSlot {
  readonly column: number;
  readonly row: number;
}

export interface RelationshipGraphCluster {
  readonly grainIds: readonly string[];
  readonly origin: RelationshipGraphGridSlot;
}

export function relationshipGraphClusterSlots({
  grainIds,
  origin,
}: {
  grainIds: readonly string[];
  origin: RelationshipGraphGridSlot;
}): Record<string, RelationshipGraphGridSlot> {
  const offsets = relationshipGraphClusterOffsets(grainIds.length);
  const slots: Record<string, RelationshipGraphGridSlot> = {};
  for (const [index, grainId] of grainIds.entries()) {
    const offset = offsets[index];
    if (!offset) {
      continue;
    }
    slots[grainId] = {
      column: origin.column + offset.column,
      row: origin.row + offset.row,
    };
  }
  return slots;
}

function relationshipGraphClusterOffsets(
  count: number,
): readonly RelationshipGraphGridSlot[] {
  switch (count) {
    case 1:
      return [{ column: 0, row: 0 }];
    case 2:
      return [
        { column: 0, row: 0 },
        { column: 1, row: 0 },
      ];
    case 3:
      return [
        { column: 0, row: 0 },
        { column: 0, row: 1 },
        { column: 1, row: 1 },
      ];
    case 4:
      return [
        { column: 0, row: 0 },
        { column: 1, row: 0 },
        { column: 0, row: 1 },
        { column: 1, row: 1 },
      ];
    default:
      return Array.from({ length: count }, (_, index) => ({
        column: index % 2,
        row: Math.floor(index / 2),
      }));
  }
}

export const RELATIONSHIP_GRAPH_PREFERRED_CLUSTERS: readonly RelationshipGraphCluster[] =
  [
    {
      grainIds: [
        "fulfillment_group_fulfillment_orders",
        "fulfillment_groups",
        "fulfillment_group_shipment_surcharges",
        "fulfillment_group_shipments",
      ],
      origin: { column: 2, row: 2.5 },
    },
    {
      grainIds: ["campaigns", "automations", "marketing_outreach_events"],
      origin: { column: 2, row: 4.5 },
    },
  ] as const;

export const RELATIONSHIP_GRAPH_PREFERRED_GRID_SLOTS: Readonly<
  Record<string, RelationshipGraphGridSlot>
> = {
  orders: { column: 0, row: 0 },
  order_line_items: { column: 0, row: 1 },
  checkout_widget_attributed_order_line_items: { column: 0, row: 2 },
  checkout_widgets: { column: -1, row: 2 },
  post_purchase_events: { column: -1, row: 1 },
  upsell_pages: { column: -2, row: 1 },
  order_line_item_properties: { column: 1, row: 2 },
  returns: { column: 1, row: 0 },
  return_line_items: { column: 1, row: 1 },
  return_shipments: { column: 2, row: 0 },
} as const;

export const METADATA_COLLAPSED_PREVIEW_LENGTH = 220;

export const Last30DaysOption: RedoListItem<number> = {
  id: "30",
  text: "Last 30 days",
  type: "text",
  value: 30,
};

export const lookbackOptions: RedoListItem<number>[] = [
  { id: "7", text: "Last 7 days", type: "text", value: 7 },
  Last30DaysOption,
  { id: "90", text: "Last 90 days", type: "text", value: 90 },
  { id: "365", text: "Last 365 days", type: "text", value: 365 },
];

// Tokenizes enough JSON for readable syntax highlighting. JSON.parse still owns
// the actual string unescaping in the classifier.
export const JSON_CODE_TOKEN_PATTERN =
  /("(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null|[{}[\]:,])/g;

// Tokenizes enough Snowflake SQL for readable highlighting and catalog hovers.
// This is intentionally a highlighter pattern, not a full SQL parser.
export const SQL_CODE_TOKEN_PATTERN =
  /('(?:''|[^'])*'|"(?:""|[^"])*"|[A-Za-z_][A-Za-z0-9_$]*|-?\d+(?:\.\d+)?|<=|>=|<>|!=|[(),.*=+\-/])/g;

// Classifies scalar code tokens without re-running the larger tokenizer regexes.
export const NUMBER_CODE_TOKEN_PATTERN = /^-?\d/;
export const SQL_IDENTIFIER_START_PATTERN = /^[A-Za-z_]/;
export const SQL_OPERATOR_TOKEN_PATTERN = /^[(),.*=+\-/<>!]+$/;
export const WHITESPACE_PATTERN = /\s/;

// Snowflake escapes a double quote inside a quoted identifier by doubling it.
export const SQL_ESCAPED_IDENTIFIER_QUOTE_PATTERN = /""/g;

// Normalizes catalog lookup labels that may arrive quoted from JSON or SQL.
export const CATALOG_LOOKUP_OUTER_QUOTE_PATTERN = /^["']|["']$/g;
export const CATALOG_LOOKUP_DOUBLE_QUOTE_PATTERN = /\\?"/g;

// Converts internal catalog keys into readable labels.
export const CAMEL_CASE_BOUNDARY_PATTERN = /([a-z0-9])([A-Z])/g;
export const UNDERSCORE_PATTERN = /_/g;
export const WORD_INITIAL_PATTERN = /\b\w/g;

// Compacts catalog search strings so spaced, underscored, dotted, and camel-ish
// user input can still match the same semantic field.
export const CATALOG_SEARCH_SEPARATOR_PATTERN = /[^a-z0-9]+/g;

export const SqlKeywords = new Set(SnowflakeCompilerSqlTokenWords);

export const CatalogLookupStopwords = new Set([
  "all",
  "and",
  "any",
  "array",
  "as",
  "by",
  "date",
  "false",
  "field",
  "for",
  "id",
  "in",
  "item",
  "kind",
  "label",
  "metric",
  "name",
  "null",
  "number",
  "of",
  "on",
  "ref",
  "table",
  "text",
  "true",
  "type",
  "value",
]);

export const CatalogLookupDetailKeys = new Set([
  "baseTable",
  "field",
  "grain",
  "namespace",
  "owningTable",
  "physicalFieldName",
  "physicalTable",
  "refKey",
  "sourceTable",
  "table",
  "value",
]);
